/* Anirveda product page (Foxtale clone): gallery, toggles, tabs, compare sliders, accordions, share, add to cart.
   Add to cart reuses Horizon's cart events so the theme's cart drawer and cart count update. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
// Horizon declares `Theme` as a global const (not on window).
const routes = (typeof Theme !== 'undefined' && Theme.routes) || { cart_add_url: shopRoot + 'cart/add.js', cart_url: shopRoot + 'cart' };

const qa = (root, sel) => Array.from(root.querySelectorAll(sel));

function initMobileGallery(gal) {
  const track = gal.querySelector('.ani-fx-gal__track');
  if (!track || track.dataset.ready) return;
  track.dataset.ready = '1';
  const slides = qa(track, '.ani-fx-gal__slide');
  const dots = qa(gal, '.ani-fx-gal__dot');
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (!e.isIntersecting) return;
    const i = slides.indexOf(e.target);
    dots.forEach((d, j) => d.setAttribute('aria-current', String(i === j)));
  }), { root: track, threshold: 0.6 });
  slides.forEach((s) => io.observe(s));
}

function initDesktopGallery(gal) {
  if (gal.dataset.ready) return;
  gal.dataset.ready = '1';
  const imgs = qa(gal, '.ani-fx-gal-d__main img');
  const thumbs = qa(gal, '.ani-fx-thumbs__item');
  const list = gal.querySelector('.ani-fx-thumbs__list');
  let index = 0;
  const show = (i) => {
    index = (i + imgs.length) % imgs.length;
    gal.dataset.index = String(index);
    imgs.forEach((img, j) => { img.hidden = j !== index; });
    thumbs.forEach((t, j) => t.setAttribute('aria-current', String(j === index)));
    const visible = 4;
    const start = Math.min(Math.max(0, index - visible + 1), Math.max(0, thumbs.length - visible));
    thumbs.forEach((t, j) => { t.hidden = j < start || j >= start + visible; });
  };
  thumbs.forEach((t, j) => t.addEventListener('click', () => show(j)));
  qa(gal, '[data-fx-step]').forEach((b) => b.addEventListener('click', () => show(index + Number(b.dataset.fxStep))));
  if (list && imgs.length) show(0);
}

function initCompare(cmp) {
  if (cmp.dataset.ready) return;
  cmp.dataset.ready = '1';
  const handle = cmp.querySelector('.ani-fx-cmp__handle');
  if (!handle) return;
  const set = (p) => {
    const v = Math.min(100, Math.max(0, p));
    cmp.style.setProperty('--fx-pos', v + '%');
    handle.setAttribute('aria-valuenow', String(Math.round(v)));
    cmp.dataset.moved = 'true';
  };
  cmp.fxSet = set;
  const fromEvent = (e) => {
    const r = cmp.getBoundingClientRect();
    set(((e.clientX - r.left) / r.width) * 100);
  };
  handle.addEventListener('pointerdown', (e) => {
    handle.setPointerCapture(e.pointerId);
    const move = (ev) => fromEvent(ev);
    const up = () => { handle.removeEventListener('pointermove', move); handle.removeEventListener('pointerup', up); };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
  });
  handle.addEventListener('keydown', (e) => {
    const now = Number(handle.getAttribute('aria-valuenow') || 50);
    const map = { ArrowLeft: now - 5, ArrowRight: now + 5, Home: 0, End: 100 };
    if (e.key in map) { e.preventDefault(); set(map[e.key]); }
  });
}

/* Full-screen photo carousel: opens on the tapped photo, swipe / arrows / thumbnails / keys, Esc or ✕ closes */
function lightbox(section) {
  const lb = section && section.querySelector('.ani-fx-lb');
  if (!lb) return null;
  if (lb.dataset.ready) return lb;
  lb.dataset.ready = '1';
  const track = lb.querySelector('.ani-fx-lb__track');
  const slides = qa(track, '.ani-fx-lb__slide');
  const thumbs = qa(lb, '.ani-fx-lb__thumb');
  const count = lb.querySelector('.ani-fx-lb__count');
  const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
  const mark = () => {
    const i = current();
    if (count) count.textContent = `${i + 1} / ${slides.length}`;
    thumbs.forEach((t, j) => t.setAttribute('aria-current', String(i === j)));
  };
  lb.fxGo = (i, smooth = true) => {
    const n = (i + slides.length) % slides.length;
    track.scrollTo({ left: n * track.clientWidth, behavior: smooth ? 'smooth' : 'instant' });
    if (!smooth) mark();
  };
  const unlock = () => document.documentElement.classList.remove('ani-fx-lb-open');
  const shut = () => { lb.close(); unlock(); };
  let raf;
  track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(mark); });
  lb.addEventListener('cancel', unlock);
  lb.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest('[data-fx-lb-close]') || t === lb) shut();
    else if (t.closest('[data-fx-lb-step]')) lb.fxGo(current() + Number(t.closest('[data-fx-lb-step]').dataset.fxLbStep));
    else if (t.closest('.ani-fx-lb__thumb')) lb.fxGo(thumbs.indexOf(t.closest('.ani-fx-lb__thumb')));
  });
  lb.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') lb.fxGo(current() + 1);
    if (e.key === 'ArrowLeft') lb.fxGo(current() - 1);
  });
  lb.addEventListener('close', unlock);
  return lb;
}

function openLightbox(section, index) {
  const lb = lightbox(section);
  if (!lb || typeof lb.showModal !== 'function') return;
  document.documentElement.classList.add('ani-fx-lb-open');
  lb.showModal();
  setTimeout(() => lb.fxGo(index, false), 0);
}

async function addToCart(button) {
  const items = JSON.parse(button.dataset.fxItems || '[]');
  if (!items.length || button.getAttribute('aria-busy') === 'true') return;
  const msg = document.querySelector('.ani-fx-sticky__msg');
  const label = button.querySelector('[data-fx-label]') || button;
  const original = label.textContent;
  button.setAttribute('aria-busy', 'true');
  if (msg) msg.textContent = '';

  const fd = new FormData();
  items.forEach((it, i) => {
    fd.append(`items[${i}][id]`, it.id);
    fd.append(`items[${i}][quantity]`, it.quantity || 1);
  });
  const sectionIds = qa(document, 'cart-items-component').map((el) => el.dataset.sectionId).filter(Boolean);
  if (sectionIds.length) fd.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity || 1 })),
    promise: deferred.promise,
  }));

  try {
    const res = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd,
    }).then((r) => r.json());
    if (res.status) {
      button.dispatchEvent(new CartErrorEvent({ error: res.message || 'Add to cart failed', code: 'INVALID' }));
      deferred.resolve({ detail: { didError: true } });
      if (msg) msg.textContent = res.description || res.message || 'Could not add this to the cart.';
      return;
    }
    const cart = await fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'ani-fx',
        sourceId: button.id || 'ani-fx',
        itemCount: items.reduce((n, it) => n + (it.quantity || 1), 0),
        productId: button.dataset.fxProduct,
        sections: res.sections,
        didError: false,
      },
    });
    if (button.dataset.fxAdded) {
      label.textContent = button.dataset.fxAdded;
      setTimeout(() => { label.textContent = original; }, 2000);
    }
  } catch (err) {
    deferred.reject(err);
    if (msg) msg.textContent = 'Something went wrong. Please try again.';
  } finally {
    button.removeAttribute('aria-busy');
  }
}

function init(root = document) {
  qa(root, '.ani-fx-gal').forEach(initMobileGallery);
  qa(root, '.ani-fx-gal-d').forEach(initDesktopGallery);
  qa(root, '.ani-fx-cmp').forEach(initCompare);
}

document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t || !t.closest('.ani-fx')) return;

  const add = t.closest('[data-fx-items]');
  if (add) { e.preventDefault(); addToCart(add); return; }

  if (!t.closest('button, a, .ani-fx-lb')) {
    const slide = t.closest('.ani-fx-gal__slide');
    if (slide) { openLightbox(slide.closest('.ani-fx-main'), qa(slide.parentElement, '.ani-fx-gal__slide').indexOf(slide)); return; }
    const main = t.closest('.ani-fx-gal-d__main');
    if (main) { openLightbox(main.closest('.ani-fx-main'), Number(main.closest('.ani-fx-gal-d').dataset.index || 0)); return; }
  }

  const toggle = t.closest('[data-fx-how]');
  if (toggle) {
    const on = toggle.getAttribute('aria-checked') !== 'true';
    toggle.setAttribute('aria-checked', String(on));
    toggle.closest('.ani-fx-ingr').dataset.how = String(on);
    return;
  }

  const more = t.closest('[data-fx-more]');
  if (more) {
    const box = more.closest('.ani-fx-desc');
    const open = box.dataset.open !== 'true';
    box.dataset.open = String(open);
    more.setAttribute('aria-expanded', String(open));
    more.textContent = open ? more.dataset.less : more.dataset.more;
    return;
  }

  const code = t.closest('[data-fx-code]');
  if (code) {
    navigator.clipboard?.writeText(code.dataset.fxCode).catch(() => {});
    code.dataset.copied = 'true';
    setTimeout(() => { code.dataset.copied = 'false'; }, 2000);
    return;
  }

  const share = t.closest('[data-fx-share]');
  if (share) {
    const data = { title: document.title, url: share.dataset.fxShare };
    if (navigator.share) navigator.share(data).catch(() => {});
    else navigator.clipboard?.writeText(data.url).catch(() => {});
    return;
  }

  const tab = t.closest('[role="tab"][data-fx-tab]');
  if (tab) {
    const list = tab.closest('[role="tablist"]');
    const wrap = tab.closest('.ani-fx-about');
    qa(list, '[role="tab"]').forEach((b) => {
      const sel = b === tab;
      b.setAttribute('aria-selected', String(sel));
      b.tabIndex = sel ? 0 : -1;
      const panel = wrap.querySelector('#' + b.getAttribute('aria-controls'));
      if (panel) panel.hidden = !sel;
    });
    return;
  }

  const side = t.closest('[data-fx-side]');
  if (side) {
    const cmp = side.closest('.ani-fx-results').querySelector('.ani-fx-cmp');
    qa(side.parentElement, '[data-fx-side]').forEach((b) => b.setAttribute('aria-pressed', String(b === side)));
    if (cmp && cmp.fxSet) cmp.fxSet(side.dataset.fxSide === 'before' ? 100 : 0);
    return;
  }

  const acc = t.closest('[data-fx-acc]');
  if (acc) {
    const open = acc.getAttribute('aria-expanded') !== 'true';
    acc.setAttribute('aria-expanded', String(open));
    const body = document.getElementById(acc.getAttribute('aria-controls'));
    if (body && body.classList.contains('ani-fx-collapse')) {
      body.dataset.open = String(open);
      body.inert = !open;
    } else if (body) body.hidden = !open;
  }
});

document.addEventListener('keydown', (e) => {
  const tab = e.target instanceof Element && e.target.closest('[role="tab"][data-fx-tab]');
  if (!tab || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
  const tabs = qa(tab.closest('[role="tablist"]'), '[role="tab"]');
  const next = tabs[(tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
  next.focus();
  next.click();
});

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
